import type { MessageView } from "../api/types";
import { useCharacter, useOptionalCharacter } from "../character";
import { source, text } from "../text";

const severityLabel = { error: "Fehler", warning: "Hinweis", info: "Info" } as const;

/**
 * Runs the operation a message offers to resolve it. The notice afterwards names what happened and offers to undo it, since an action may
 * remove things, e.g. a pack animal whose resource is gone.
 */
export function useQuickAction(): (message: MessageView) => void {
  const { choose, language } = useCharacter();
  return (m) => {
    if (m.message.action) void choose({ type: m.message.action.operation, payload: m.message.action.payload }, text(m.message.action.label, language));
  };
}

export function MessageList(props: {
  messages: MessageView[];
  compact?: boolean;
  onAcknowledge?: (message: MessageView) => void;
  onUnacknowledge?: (message: MessageView) => void;
  onNavigate?: (message: MessageView) => void;
  /** Whether a message has a place to go to; by default its assistant step. */
  canNavigate?: (message: MessageView) => boolean;
  /** Runs the operation a message offers to resolve it. */
  onAction?: (message: MessageView) => void;
  /**
   * The list offers approvals although the character is read-only: the approvals of a campaign's game master, outside the disabled
   * sheet. Elsewhere a read-only character shows who acknowledged, without buttons.
   */
  approvals?: boolean;
}) {
  // The server says who may approve: the admin or the game master of a campaign the owner shared the character with (decision E51).
  const character = useOptionalCharacter()?.character;
  const gameMaster = !!character?.canAcknowledge && (!character.readOnly || !!props.approvals);
  const readOnly = !!character?.readOnly && !props.approvals;
  if (props.messages.length === 0) return null;
  return (
    <ul className={`messages${props.compact ? " compact" : ""}`}>
      {props.messages.map((m) => (
        <li key={m.key} className={`message ${m.message.severity ?? "error"}${m.acknowledged ? " acknowledged" : ""}`}>
          <span className="message-severity">{severityLabel[m.message.severity ?? "error"]}</span>
          <span className="message-text">
            {text(m.message.text)}
            {(m.message.sources ?? []).map((s, i) => (
              <span key={i} className="source">
                {" "}
                {source(s)}
              </span>
            ))}
          </span>
          <span className="message-actions">
            {props.onNavigate && (props.canNavigate ? props.canNavigate(m) : !!m.message.target?.step) && (
              <button type="button" className="link" onClick={() => props.onNavigate!(m)}>
                zur Stelle
              </button>
            )}
            {m.message.action && props.onAction && (
              <button type="button" className="link" onClick={() => props.onAction!(m)}>
                {text(m.message.action.label)}
              </button>
            )}
            {m.message.acknowledgeable && !m.acknowledged && props.onAcknowledge && gameMaster && (
              <button type="button" className="link" onClick={() => props.onAcknowledge!(m)}>
                SL hat zugestimmt
              </button>
            )}
            {m.message.acknowledgeable && !m.acknowledged && props.onAcknowledge && !gameMaster && !readOnly && (
              <span className="muted small">Zustimmen kann nur die Spielleitung.</span>
            )}
            {m.acknowledged && m.acknowledgedBy && m.acknowledgedBy !== "user" && (
              <span className="muted small">quittiert von {m.acknowledgedBy}</span>
            )}
            {m.acknowledged && props.onUnacknowledge && gameMaster && (
              <button type="button" className="link" onClick={() => props.onUnacknowledge!(m)}>
                Quittung zurücknehmen
              </button>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
